import apiClient from './apiClient';

class InvoiceService {
    // Get all invoices with filters
    async getAllInvoices(params = {}) {
        try {
            const response = await apiClient.get('/invoices', { params });
            return {
                success: true,
                data: response.data.data,
                meta: response.data.meta
            };
        } catch (error) {
            console.error('Error fetching invoices:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch invoices'
            };
        }
    }

    // Get invoice by ID
    async getInvoiceById(invoiceId) {
        try {
            const response = await apiClient.get(`/invoices/${invoiceId}`);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error fetching invoice:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch invoice'
            };
        }
    }

    // Generate new invoice
    async generateInvoice(invoiceData) {
        try {
            const response = await apiClient.post('/invoices', invoiceData);
            return {
                success: true,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            console.error('Error generating invoice:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to generate invoice',
                errors: error.response?.data?.errors
            };
        }
    }

    // Update invoice status
    async updateInvoiceStatus(invoiceId, statusData) {
        try {
            const response = await apiClient.post(`/invoices/${invoiceId}/update-status`, statusData);
            return {
                success: true,
                message: response.data.message
            };
        } catch (error) {
            console.error('Error updating invoice status:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update invoice status',
                errors: error.response?.data?.errors
            };
        }
    }

    // Get unbilled bookings for a customer
    async getUnbilledBookings(params = {}) {
        try {
            const response = await apiClient.get('/invoice-data/unbilled-bookings', { params });
            return {
                success: true,
                data: response.data.data,
                meta: response.data.meta
            };
        } catch (error) {
            console.error('Error fetching unbilled bookings:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch unbilled bookings'
            };
        }
    }

    // Get invoice statistics
    async getInvoiceStats(params = {}) {
        try {
            const response = await apiClient.get('/invoice-data/stats', { params });
            const data = response.data.data || {};
            
            // Normalize NaN/undefined to 0
            const safeNumber = (v) => {
                const result = Number.isFinite(Number(v)) ? Number(v) : 0;
                return result;
            };
            const normalized = {
                totals: {
                    invoices: safeNumber(data?.totals?.invoices ?? data?.total_invoices),
                    amount: safeNumber(data?.totals?.amount ?? data?.total_amount)
                },
                by_status: {
                    pending: {
                        count: safeNumber(data?.by_status?.pending?.count ?? data?.pending_invoices),
                        amount: safeNumber(data?.by_status?.pending?.amount ?? data?.pending_amount)
                    },
                    paid: {
                        count: safeNumber(data?.by_status?.paid?.count ?? data?.paid_invoices),
                        amount: safeNumber(data?.by_status?.paid?.amount ?? data?.paid_amount)
                    },
                    overdue: {
                        count: safeNumber(data?.by_status?.overdue?.count ?? data?.overdue_invoices),
                        amount: safeNumber(data?.by_status?.overdue?.amount ?? data?.overdue_amount)
                    },
                    cancelled: {
                        count: safeNumber(data?.by_status?.cancelled?.count ?? data?.cancelled_invoices),
                        amount: safeNumber(data?.by_status?.cancelled?.amount ?? data?.cancelled_amount)
                    }
                },
                filter_info: data?.filter_info || {
                    has_filters: false,
                    from_date: null,
                    to_date: null,
                    from_due_date: null,
                    to_due_date: null,
                    is_default_range: false
                },
                debug: data?.debug || {}
            };
            return {
                success: true,
                data: normalized
            };
        } catch (error) {
            console.error('Error fetching invoice statistics:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch invoice statistics'
            };
        }
    }

    // Get customers with unbilled bookings
    async getCustomersWithUnbilledBookings() {
        try {
            const response = await apiClient.get('/invoice-data/customers-with-unbilled');
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error fetching customers with unbilled bookings:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch customers'
            };
        }
    }

    // Search customers for invoice generation
    async searchCustomers(searchTerm) {
        try {
            const response = await apiClient.get('/invoice-data/search-customers', {
                params: { search: searchTerm }
            });
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error searching customers:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to search customers'
            };
        }
    }

    // Preview invoice before generation
    async previewInvoice(previewData) {
        try {
            const response = await apiClient.post('/invoice-data/preview', previewData);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error generating invoice preview:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to generate invoice preview'
            };
        }
    }

    // To-pay invoices: recipients who are not in the customer master, billed by GST number

    // Search to-pay parties (recipient GSTIN or name) with delivered, unbilled bookings
    async searchToPayParties(searchTerm, { from_date, to_date } = {}) {
        try {
            const response = await apiClient.get('/invoice-data/to-pay/search-parties', {
                params: { search: searchTerm, from_date: from_date || undefined, to_date: to_date || undefined }
            });
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error searching to-pay parties:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to search to-pay customers'
            };
        }
    }

    async previewToPayInvoice(previewData) {
        try {
            const response = await apiClient.post('/invoice-data/to-pay/preview', previewData);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error generating to-pay invoice preview:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to generate invoice preview'
            };
        }
    }

    async generateToPayInvoice(invoiceData) {
        try {
            const response = await apiClient.post('/invoices/to-pay', invoiceData);
            return {
                success: true,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            console.error('Error generating to-pay invoice:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to generate invoice',
                errors: error.response?.data?.errors
            };
        }
    }

    // Get invoice summary
    async getInvoiceSummary(params = {}) {
        try {
            const response = await apiClient.get('/invoice-data/summary', { params });
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error fetching invoice summary:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch invoice summary'
            };
        }
    }

    // Utility methods
    formatCurrency(amount) {
        if (!amount || isNaN(amount)) return '₹0.00';
        const numAmount = parseFloat(amount);
        if (isNaN(numAmount)) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(numAmount);
    }

    formatDate(dateString) {
        if (!dateString) return '-';

        try {
            // Handle different date formats that might come from backend
            let date;

            // If it's already a Date object
            if (dateString instanceof Date) {
                date = dateString;
            }
            // If it's a string in YYYY-MM-DD format
            else if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                date = new Date(dateString + 'T00:00:00');
            }
            // If it's a string in DD/MM/YYYY or DD/M/YYYY format (from legacy backend)
            else if (typeof dateString === 'string' && dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                const parts = dateString.split('/');
                date = new Date(parts[2], parts[1] - 1, parts[0]);
            }
            // Try parsing as is
            else {
                date = new Date(dateString);
            }

            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.warn('Invalid date format:', dateString);
                return dateString; // Return original string if parsing fails
            }

            return date.toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return dateString || '-';
        }
    }

    getStatusColor(status) {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'warning';
            case 'paid':
                return 'success';
            case 'overdue':
                return 'error';
            case 'cancelled':
                return 'default';
            default:
                return 'info';
        }
    }

    getStatusLabel(status) {
        const statusLabels = {
            'pending': 'Pending',
            'paid': 'Paid',
            'overdue': 'Overdue',
            'cancelled': 'Cancelled'
        };
        return statusLabels[status] || status;
    }

    validateInvoiceData(invoiceData) {
        const errors = {};

        if (invoiceData.invoice_type === 'to_pay') {
            if (!invoiceData.gst_no) {
                errors.gst_no = 'GST number is required';
            }
        } else if (!invoiceData.customer_id) {
            errors.customer_id = 'Customer is required';
        }

        // Date fields are optional - if provided, validate them
        if (invoiceData.from_date && invoiceData.to_date) {
            const fromDate = new Date(invoiceData.from_date);
            const toDate = new Date(invoiceData.to_date);
            
            if (fromDate > toDate) {
                errors.to_date = 'To date must be after from date';
            }
        }

        // Validate invoice date if provided
        if (invoiceData.invoice_date && invoiceData.invoice_date.trim()) {
            const invoiceDate = new Date(invoiceData.invoice_date);
            const today = new Date();

            // Check if date is valid
            if (isNaN(invoiceDate.getTime())) {
                errors.invoice_date = 'Invalid invoice date';
            } else if (invoiceDate > today) {
                errors.invoice_date = 'Invoice date cannot be in the future';
            }
        }

        // Validate custom invoice number if provided
        if (invoiceData.custom_invoice_no && invoiceData.custom_invoice_no.trim()) {
            const customInvoiceNo = invoiceData.custom_invoice_no.trim();

            // Check length
            if (customInvoiceNo.length > 50) {
                errors.custom_invoice_no = 'Custom invoice number cannot exceed 50 characters';
            }

            // Check format (only letters, numbers, hyphens, underscores, and forward slashes)
            const validFormat = /^[A-Za-z0-9\-_\/]+$/.test(customInvoiceNo);
            if (!validFormat) {
                errors.custom_invoice_no = 'Custom invoice number can only contain letters, numbers, hyphens, underscores, and forward slashes';
            }
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    // Calculate days between dates
    calculateDaysBetween(startDate, endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    // Check if invoice is overdue
    isOverdue(dueDate, status) {
        return status === 'overdue';
    }

    // Calculate overdue days
    getOverdueDays(dueDate, status) {
        if (!this.isOverdue(dueDate, status)) return 0;
        const today = new Date();
        const due = new Date(dueDate);
        const diffTime = today - due;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
}

// Export singleton instance
export const invoiceService = new InvoiceService();
export default invoiceService; 